/**
 * The machine half of a refusal, behind one toggle (Bundle 2 item 2).
 *
 * `RefusalPanel` used to render all of this open: the error code, the raw
 * `refusal_reason` token, and up to six diagnostic sections stacked under each
 * other. Every one of them is worth keeping — support cannot triage a refusal
 * without the code, and the deny-list hits are the only place the CPA sees the
 * exact sentence that tripped and its permitted equivalent — but with all of it
 * open at once, the first thing on the screen after "Refused" was a red wall,
 * and the one line that says what to do about it was somewhere in the middle.
 *
 * So the panel above keeps the human half — headline, the server's own message,
 * the single remedy — and everything that is really addressed to an engineer
 * lives here, collapsed until asked for.
 *
 * Two deliberate choices:
 *
 * - It is a native `<details>`, not React state. Collapsed children stay in the
 *   DOM, so nothing here is hidden from a screen reader's search, from Ctrl-F,
 *   or from the existing tests that assert on the wall's `textContent`. Nothing
 *   is *removed* from the page by this change; it is moved one click away.
 * - The `<details>` renders unconditionally, because the error code always
 *   exists. However empty the payload, "what do I quote to support?" always has
 *   an answer on this screen.
 *
 * Everything rendered is still the server's own prose. There is no local copy of
 * the deny list, no local rationale text, and no code→advice mapping — the
 * policy owns all of it, so a policy edit changes this screen with no deploy.
 *
 * Two payload shapes share the key `problems`: the allow-list gate sends
 * objects, the issuer-eligibility gate sends strings. Both are rendered.
 *
 * The evidence-gate lists come first inside the toggle, ahead of the deny-list
 * hits, because they are the only sections that name a *value* the CPA can go
 * and check. `core/` has computed `gate`, `observed` and `expected` on every
 * finding since the gates were built and the API has always returned them;
 * this screen simply did not draw them, so an evidence refusal said only that
 * the file did not verify and never which of entity, period or figure was the
 * one that disagreed. Nothing about the matching changed to fix that — this is
 * a rendering of data that was already on the wire.
 *
 * The row and card components live in `refusalDetailParts.tsx`; this file owns
 * only which lists exist and in what order.
 */
import type { AssuranceHit, AttestRefusal, DeniedHit, GateFinding } from '@/components/attest/api'
import { INDEPENDENCE_CODE_MEANINGS } from '@/components/attest/independenceHelp'

import {
  asArray, str, Section, Quoted, DeniedHitCard, AssuranceHitCard,
  GateFindingCard, ProblemList,
} from '@/components/attest/refusalDetailParts'

export function RefusalDetails({ refusal }: { refusal: AttestRefusal }) {
  const d = refusal.detail
  const deniedHits = asArray(d.denied_hits) as DeniedHit[]
  const assuranceHits = asArray(d.assurance_hits) as AssuranceHit[]
  const problems = asArray(d.problems)
  const missingInputs = asArray(d.missing)
  const missingEvidence = asArray(d.missing_evidence)
  const impairments = asArray(d.impairments)
  // The evidence gates' own findings. `evidence_findings` is what blocked the
  // build; `needs_review` is what a human still has to look at; the third list
  // is statements nothing spoke to at all, which by construction never overlaps
  // the first (see `EvidenceNotVerifiedDetail`).
  const gateFindings = asArray(d.evidence_findings) as GateFinding[]
  const needsReview = asArray(d.needs_review) as GateFinding[]
  const unverified = asArray(d.unverified_statements)

  return (
    <details
      data-testid="attest-refusal-details"
      className="mt-4 rounded border border-red-200 bg-white/70 px-3 py-2"
    >
      {/* `list-none` plus the caret we draw ourselves: the native marker is the
          one element here Tailwind cannot colour, and a black triangle on a red
          card was the only thing on the screen not part of the palette. */}
      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold uppercase tracking-wide text-red-900/70 [&::-webkit-details-marker]:hidden">
        <span aria-hidden className="transition-transform">▸</span>
        Details — error code and what the engine matched
      </summary>

      <div className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="attest-refusal-code"
            className="rounded bg-red-900 px-2 py-1 font-mono text-[11px] text-white"
          >
            {refusal.error_code}
          </span>
          {str(d.refusal_reason) && (
            <span className="rounded bg-red-100 px-2 py-1 font-mono text-xs text-red-800">
              {str(d.refusal_reason)}
            </span>
          )}
        </div>

        {gateFindings.length > 0 && (
          <Section title="What did not line up">
            {gateFindings.map((f, i) => (
              <GateFindingCard key={`${f.gate}-${f.assertion_id}-${i}`} finding={f} />
            ))}
          </Section>
        )}

        {needsReview.length > 0 && (
          <Section title="Checks a person still has to make">
            {needsReview.map((f, i) => (
              <GateFindingCard key={`${f.gate}-${f.assertion_id}-${i}`} finding={f} />
            ))}
          </Section>
        )}

        {unverified.length > 0 && (
          <Section title="Statements nothing on file verifies">
            <ul className="space-y-2">
              {unverified.map((u, i) => {
                const row = (u ?? {}) as Record<string, unknown>
                return (
                  <li key={i} className="rounded border border-red-200 bg-red-50/60 p-3">
                    <p className="text-sm text-red-900">
                      <span className="font-mono text-xs">{str(row.assertion_id)}</span>
                      {str(row.match_status) && (
                        <span className="ml-2 rounded bg-white/70 px-2 py-0.5 font-mono text-[11px] text-gray-700">
                          {str(row.match_status)}
                        </span>
                      )}
                    </p>
                    <div className="mt-2"><Quoted text={str(row.text)} /></div>
                  </li>
                )
              })}
            </ul>
          </Section>
        )}

        {deniedHits.length > 0 && (
          <Section title="Assertions on the deny list">
            {deniedHits.map((h, i) => <DeniedHitCard key={`${h.assertion_id}-${i}`} hit={h} />)}
          </Section>
        )}

        {assuranceHits.length > 0 && (
          <Section title="Assurance the engagement does not support">
            {assuranceHits.map((h, i) => <AssuranceHitCard key={`${h.phrase_id}-${i}`} hit={h} />)}
          </Section>
        )}

        {problems.length > 0 && (
          <Section title="What blocked it">
            <ProblemList problems={problems} />
          </Section>
        )}

        {missingInputs.length > 0 && (
          <Section title="Missing information">
            <ul className="space-y-2">
              {missingInputs.map((m, i) => {
                if (typeof m === 'string') {
                  return <li key={i} className="font-mono text-sm text-red-900">{m}</li>
                }
                const row = (m ?? {}) as Record<string, unknown>
                return (
                  <li key={i} className="text-sm text-gray-800">
                    <span className="font-semibold">{str(row.label) || str(row.key)}</span>
                    {str(row.description) && <> — {str(row.description)}</>}
                  </li>
                )
              })}
            </ul>
          </Section>
        )}

        {missingEvidence.length > 0 && (
          <Section title="Statements with no traceable source">
            <ul className="space-y-2">
              {missingEvidence.map((m, i) => {
                const row = (m ?? {}) as Record<string, unknown>
                return (
                  <li key={i} className="rounded border border-red-200 bg-red-50/60 p-3">
                    <p className="text-sm text-red-900">
                      <span className="font-mono text-xs">{str(row.assertion_id)}</span> —{' '}
                      {str(row.reason)}
                    </p>
                    <div className="mt-2"><Quoted text={str(row.text)} /></div>
                  </li>
                )
              })}
            </ul>
          </Section>
        )}

        {impairments.length > 0 && (
          <Section title="Independence impairments">
            <ul className="space-y-2">
              {impairments.map((m, i) => {
                const row = (m ?? {}) as Record<string, unknown>
                const code = str(row.code)
                // Plain English first, the engine's own sentence under it: the CPA
                // needs to know what to do, the record needs the exact wording.
                const meaning = INDEPENDENCE_CODE_MEANINGS[code]
                return (
                  <li key={i} className="rounded border border-red-200 bg-red-50/60 p-3">
                    {meaning && (
                      <p className="text-sm text-red-900" data-testid={`impairment-meaning-${code}`}>
                        {meaning}
                      </p>
                    )}
                    <p className={meaning ? 'mt-1 text-sm text-gray-800' : 'text-sm text-gray-800'}>
                      {str(row.reason)}
                    </p>
                    {str(row.description) && (
                      <p className="mt-1 text-xs text-gray-600">{str(row.description)}</p>
                    )}
                    <p className="mt-1 font-mono text-[11px] text-red-800/70">{code}</p>
                  </li>
                )
              })}
            </ul>
          </Section>
        )}
      </div>
    </details>
  )
}
